import { useEffect, useState, useCallback } from 'react';
import {
  FiCopy,
  FiCheck,
  FiShield,
  FiUser,
  FiArrowDownCircle,
  FiArrowUpCircle,
  FiCheckCircle,
  FiKey,
  FiTrash2,
  FiMoreVertical,
  FiList,
  FiRefreshCw,
  FiX,
} from 'react-icons/fi';
import { api } from '../../services/api';
import { formatLocalDate } from '../../utils/formatters';
import { useI18n } from '../../hooks/useI18n';
import { DepositModal } from './DepositModal';
import { WithdrawModal } from './WithdrawModal';
import { PrivateKeyModal } from './PrivateKeyModal';
import { WhitelistModal } from './WhitelistModal';

export function WalletModal({ open, onClose, pushToast, autoExportAddress, onAutoExportComplete }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [depositModal, setDepositModal] = useState({ open: false, wallet: null });
  const [withdrawModal, setWithdrawModal] = useState({ open: false, wallet: null });
  const [pkModal, setPkModal] = useState({ open: false, privateKey: '' });
  const [whitelistModalOpen, setWhitelistModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [autoExportTriggered, setAutoExportTriggered] = useState(false);

  const formatUsdc = (value) => {
    const num = Number(value ?? 0);
    if (Number.isNaN(num)) return '$0.00';
    return `$${num.toFixed(2)}`;
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Ensure wallet exists
      await api.ensureWallet();

      // Load wallets
      const walletRes = await api.getWallets();
      if (walletRes.code === 'SUCCESS' && walletRes.data) {
        const walletsWithBalance = await Promise.all(
          walletRes.data.map(async (wallet) => {
            try {
              const addr = wallet.safe_address || wallet.wallet_address;
              const balanceRes = await api.getWalletBalance(addr);
              const balance = balanceRes?.data || {};
              const totalUsdc = (balance.usdc ?? 0) + (balance.usdc_native ?? 0);
              return {
                ...wallet,
                usdc_balance: balance.usdc ?? wallet.usdc_balance ?? 0,
                pol_balance: balance.pol ?? wallet.pol_balance ?? 0,
                usdc_native: balance.usdc_native ?? wallet.usdc_native ?? 0,
                total_usdc: totalUsdc,
              };
            } catch (err) {
              console.warn('获取钱包余额失败', err);
              return { ...wallet, total_usdc: (wallet.usdc_balance ?? 0) + (wallet.usdc_native ?? 0) };
            }
          })
        );

        const sorted = [...walletsWithBalance].sort((a, b) => {
          if (a.is_trading_wallet === b.is_trading_wallet) return 0;
          return a.is_trading_wallet ? -1 : 1;
        });
        setWallets(sorted);
      }

      // Load whitelist
      const whitelistRes = await api.getWhitelist();
      if (whitelistRes.code === 'SUCCESS' && whitelistRes.data) {
        setWhitelist(whitelistRes.data);
      }
    } catch (err) {
      setError(err.message || t('wallet.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, loadData]);

  useEffect(() => {
    if (!open) {
      setAutoExportTriggered(false);
    }
  }, [open, autoExportAddress]);

  // Click outside to close menu
  useEffect(() => {
    const handleClickOutside = () => setMenuOpen(null);
    if (menuOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedAddress(text);
      pushToast?.(t('wallet.addressCopied'), 'success');
      setTimeout(() => setCopiedAddress(null), 2000);
    });
  };

  const handleSetTradingWallet = async (walletAddress) => {
    try {
      setLoading(true);
      await api.setTradingWallet(walletAddress);
      pushToast?.(t('wallet.setTradingWalletOk'), 'success');
      await loadData();
    } catch (err) {
      pushToast?.(err.message || t('wallet.setFailed'), 'error');
      setLoading(false);
    }
  };

  const handleExportKey = useCallback(async (wallet) => {
    setMenuOpen(null);
    try {
      const addr = wallet.safe_address || wallet.wallet_address;
      const res = await api.exportPrivateKey(addr);
      if (res.code === 'SUCCESS' && res.data?.private_key) {
        setPkModal({ open: true, privateKey: res.data.private_key });
        return true;
      } else {
        // Map backend error messages to i18n keys
        let errorMsg = t('wallet.exportFailed');
        if (res.msg?.includes('Privy') || res.detail?.includes('Privy')) {
          errorMsg = t('wallet.privyExportNotSupported');
        } else if (res.msg) {
          errorMsg = res.msg;
        }
        pushToast?.(errorMsg, 'error');
        return false;
      }
    } catch (e) {
      // Handle HTTP error responses
      let errorMsg = t('wallet.requestFailed');
      if (e.message?.includes('Privy')) {
        errorMsg = t('wallet.privyExportNotSupported');
      } else if (e.message) {
        errorMsg = e.message;
      }
      pushToast?.(errorMsg, 'error');
      return false;
    }
  }, [pushToast, t]);

  useEffect(() => {
    if (!open || autoExportTriggered || !autoExportAddress) return;
    const target = wallets.find((wallet) => {
      const addr = wallet.safe_address || wallet.wallet_address;
      return addr?.toLowerCase() === autoExportAddress.toLowerCase();
    });
    if (!target) return;
    setAutoExportTriggered(true);
    handleExportKey(target).then((success) => {
      if (success) {
        onAutoExportComplete?.();
      }
    });
  }, [
    autoExportAddress,
    autoExportTriggered,
    handleExportKey,
    onAutoExportComplete,
    open,
    wallets,
  ]);

  const handleDeleteWallet = async (walletAddress) => {
    setMenuOpen(null);
    const walletToDelete = wallets.find((w) => w.wallet_address === walletAddress);
    if (walletToDelete?.is_trading_wallet) {
      pushToast?.(t('wallet.tradingWalletCannotDelete'), 'error');
      return;
    }

    if (!window.confirm(t('wallet.deleteWalletConfirm'))) return;
    try {
      await api.deleteWallet(walletAddress);
      pushToast?.(t('wallet.walletRemoved'), 'success');
      setWallets((prev) => prev.filter((w) => w.wallet_address !== walletAddress));
    } catch (e) {
      pushToast?.(t('wallet.removeFailed'), 'error');
    }
  };

  const handleAddWhitelist = async (address) => {
    if (!address.startsWith('0x') || address.length !== 42) {
      pushToast?.(t('wallet.invalidAddress'), 'error');
      return;
    }

    try {
      const res = await api.addWhitelist(address);
      if (res.code === 'SUCCESS') {
        pushToast?.(t('wallet.whitelistAdded'), 'success');
        setWhitelist((prev) => [...prev, address.toLowerCase()]);
      } else {
        pushToast?.(res.msg || t('wallet.addFailed'), 'error');
      }
    } catch (e) {
      pushToast?.(e.message || t('wallet.addFailed'), 'error');
    }
  };

  const handleRemoveWhitelist = async (address) => {
    if (!window.confirm(t('wallet.removeWhitelistConfirm'))) return;
    try {
      const res = await api.removeWhitelist(address);
      if (res.code === 'SUCCESS') {
        pushToast?.(t('wallet.whitelistRemoved'), 'success');
        setWhitelist((prev) => prev.filter((a) => a !== address));
      } else {
        pushToast?.(res.msg || t('wallet.removeFailed'), 'error');
      }
    } catch (e) {
      pushToast?.(e.message || t('wallet.removeFailed'), 'error');
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return formatLocalDate(
      isoString,
      { year: 'numeric', month: 'numeric', day: 'numeric' },
      'zh-CN'
    );
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 px-4">
        <div className="w-full max-w-2xl max-h-[85vh] rounded-3xl border border-glass-border bg-surface/95 shadow-[0_30px_80px_rgba(0,0,0,0.45)] flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 p-6 border-b border-glass-border shrink-0">
            <div className="flex items-center gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                  {t('wallet.walletManagement')}
                </div>
                <div className="mt-1 text-xl font-semibold text-white flex items-center gap-2">
                  {t('wallet.title')}
                  <button
                    onClick={loadData}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-glass-hover transition-all"
                    title={t('wallet.refresh')}
                  >
                    <FiRefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setWhitelistModalOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-highlight hover:bg-surface text-slate-300 rounded-lg text-xs font-medium transition-colors border border-glass-border"
              >
                <FiList size={14} /> {t('wallet.manageWhitelist')}
              </button>
              <button
                className="rounded-full border border-glass-border p-2 text-slate-300 hover:text-white hover:bg-glass-hover transition-colors"
                onClick={onClose}
              >
                <FiX size={16} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading && wallets.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <div className="w-8 h-8 border-3 border-accent/30 border-t-accent rounded-full animate-spin mb-3"></div>
                <p className="text-sm">{t('common.loading')}</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <p className="text-rose-400 mb-3">❌ {error}</p>
                <button
                  onClick={loadData}
                  className="px-4 py-2 rounded-lg bg-surface-highlight hover:bg-surface text-white text-sm transition-colors"
                >
                  {t('common.retry')}
                </button>
              </div>
            ) : wallets.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 rounded-2xl border-2 border-dashed border-glass-border">
                <div className="w-12 h-12 mb-3 rounded-full bg-surface-highlight flex items-center justify-center text-2xl">
                  📭
                </div>
                <p className="text-slate-400 text-sm">{t('wallet.noWallets')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {wallets.map((wallet, index) => {
                  const isSafe = wallet.wallet_type?.toLowerCase() === 'safe';
                  const displayAddress = isSafe ? wallet.safe_address : wallet.wallet_address;
                  const shortLabel = displayAddress
                    ? `${displayAddress.slice(0, 6)}...${displayAddress.slice(-4)}`
                    : `${index + 1}`;
                  const isSystemGenerated = wallet.source === 'system_generated';

                  return (
                    <div
                      key={wallet.wallet_address}
                      className={`group relative flex flex-col gap-3 p-4 rounded-2xl border transition-all duration-300 bg-surface-highlight/50 border-glass-border hover:bg-surface-highlight hover:border-accent/30 ${
                        wallet.is_trading_wallet ? 'border-accent/30 bg-accent/5' : ''
                      }`}
                    >
                      {/* Header Row */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          {/* Icon */}
                          <div
                            className={`w-9 h-9 rounded-xl flex shrink-0 items-center justify-center text-base ${
                              isSafe ? 'bg-emerald-500/10 text-emerald-400' : 'bg-indigo-500/10 text-indigo-400'
                            }`}
                          >
                            {isSafe ? <FiShield /> : <FiUser />}
                          </div>

                          {/* Info */}
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <h3 className="text-sm font-bold text-white">{shortLabel}</h3>
                              {wallet.is_trading_wallet && (
                                <span className="px-1.5 py-0.5 rounded text-[0.55rem] font-bold bg-accent text-white uppercase tracking-wide">
                                  Main
                                </span>
                              )}
                              <span className="text-[0.6rem] px-1.5 py-0.5 rounded border border-glass-border text-slate-500 bg-surface">
                                {isSafe ? 'Safe' : 'EOA'}
                              </span>
                            </div>

                            {/* Address */}
                            <div className="flex items-center gap-2 mt-1">
                              <div className="flex items-center gap-1 px-2 py-0.5 bg-surface border border-glass-border rounded-lg">
                                <code className="text-[0.65rem] text-slate-400 font-mono truncate max-w-[140px] select-all">
                                  {displayAddress}
                                </code>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopy(displayAddress);
                                  }}
                                  className="text-slate-500 hover:text-accent transition-colors"
                                >
                                  {copiedAddress === displayAddress ? <FiCheck size={10} /> : <FiCopy size={10} />}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right Side: Balances */}
                        <div className="flex flex-col items-end gap-1 text-right">
                          <div className="text-white font-semibold text-sm">{formatUsdc(wallet.total_usdc)}</div>
                          <div className="text-[0.6rem] text-slate-500">USDC</div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center justify-end gap-2 pt-2 border-t border-glass-border/50">
                        {!wallet.is_trading_wallet && (
                          <button
                            onClick={() => handleSetTradingWallet(wallet.wallet_address)}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-surface hover:bg-surface-highlight text-slate-400 hover:text-accent border border-glass-border hover:border-accent/30 rounded-lg transition-all text-[0.65rem] font-medium"
                          >
                            <FiCheckCircle size={12} />
                            <span>{t('wallet.setAsPrimary')}</span>
                          </button>
                        )}
                        <button
                          onClick={() =>
                            setDepositModal({
                              open: true,
                              wallet: wallet,
                              address: displayAddress,
                              name: shortLabel,
                              type: wallet.wallet_type,
                            })
                          }
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 hover:border-emerald-500/40 rounded-lg transition-all text-[0.65rem] font-medium"
                        >
                          <FiArrowDownCircle size={12} />
                          <span>{t('wallet.deposit')}</span>
                        </button>

                        {isSystemGenerated && (
                          <button
                            onClick={() => setWithdrawModal({ open: true, wallet: wallet })}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 hover:border-rose-500/40 rounded-lg transition-all text-[0.65rem] font-medium"
                          >
                            <FiArrowUpCircle size={12} />
                            <span>{t('wallet.withdraw')}</span>
                          </button>
                        )}

                        {/* Menu Button */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuOpen(menuOpen === wallet.wallet_address ? null : wallet.wallet_address);
                            }}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-surface-highlight transition-colors"
                          >
                            <FiMoreVertical size={14} />
                          </button>

                          {menuOpen === wallet.wallet_address && (
                            <div className="absolute right-0 bottom-full mb-1 w-36 py-1 bg-surface-highlight border border-glass-border rounded-xl shadow-xl z-10 overflow-hidden">
                              {isSystemGenerated && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleExportKey(wallet);
                                  }}
                                  className="w-full px-3 py-2 text-left text-[0.65rem] text-slate-300 hover:bg-surface hover:text-white flex items-center gap-2 transition-colors"
                                >
                                  <FiKey size={12} /> {t('wallet.exportPrivateKey')}
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteWallet(wallet.wallet_address);
                                }}
                                className="w-full px-3 py-2 text-left text-[0.65rem] text-rose-400 hover:bg-rose-900/20 hover:text-rose-300 flex items-center gap-2 transition-colors"
                              >
                                <FiTrash2 size={12} /> {t('wallet.removeWallet')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-glass-border shrink-0 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-slate-300 hover:text-white hover:bg-glass-hover transition-colors text-sm font-medium"
            >
              {t('wallet.close')}
            </button>
          </div>
        </div>
      </div>

      {/* Sub Modals */}
      <DepositModal
        open={depositModal.open}
        onClose={() => setDepositModal({ ...depositModal, open: false })}
        walletAddress={depositModal.address}
        walletName={depositModal.name}
        walletType={depositModal.type}
        pushToast={pushToast}
      />

      <WithdrawModal
        open={withdrawModal.open}
        onClose={() => setWithdrawModal({ ...withdrawModal, open: false })}
        wallet={withdrawModal.wallet}
        pushToast={pushToast}
        onSuccess={loadData}
      />

      <WhitelistModal
        open={whitelistModalOpen}
        onClose={() => setWhitelistModalOpen(false)}
        whitelist={whitelist}
        onAdd={handleAddWhitelist}
        onRemove={handleRemoveWhitelist}
        pushToast={pushToast}
      />

      <PrivateKeyModal
        open={pkModal.open}
        onClose={() => setPkModal({ ...pkModal, open: false })}
        privateKey={pkModal.privateKey}
        pushToast={pushToast}
      />
    </>
  );
}
